import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  GameState,
  PlayerResponse,
  PaymentStatusResponse,
  PlayerStatsResponse,
  FriendStatsResponse,
  PaymentMethod,
  BattleTimerResponse,
  Language,
} from '@vikings/types';
import { t } from '@vikings/game-data';
import { initTelegramApp, isTelegramEnvironment } from './telegram/init';
import {
  authenticate,
  logout,
  getPaymentStatus,
  getPlayerStats,
  getFriendStats,
  submitPayment,
  getBattleTimer,
} from './api/client';
import { GameCanvas } from './game/GameCanvas';
import { EmptyBattlefieldCanvas } from './game/EmptyBattlefieldCanvas';
import {
  LoadingScreen,
  ErrorModal,
  StatsModal,
  PaymentModal,
  BattleScheduleModal,
  InfoModal,
  LogoutModal,
  LightningLoadingOverlay,
  PasswordModal,
} from './screens/Modals';

const POLL_INTERVAL = 20_000; // 20 seconds
const STORAGE_KEY_LANG = 'vikings_lang';
const STORAGE_KEY_SESSION_CODE = 'vikings_session_code';
const MODAL_LOAD_DELAY = 350; // ms to ensure immediate lightning feedback
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Main App — manages game state, authentication, polling, and UI flow.
 */
export function App() {
  const [gameState, setGameState] = useState<GameState>('unauthorized');
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [player, setPlayer] = useState<PlayerResponse | null>(null);
  const [payment, setPayment] = useState<PaymentStatusResponse>({
    required: false,
    amount: 0,
    currency: 'coins',
    status: 'none',
  });
  const [battleTimer, setBattleTimer] = useState<BattleTimerResponse | null>(null);
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_LANG);
      if (saved === 'en' || saved === 'uk') return saved;
    } catch {
      // ignore
    }
    return 'uk';
  });
  const [playerStats, setPlayerStats] = useState<PlayerStatsResponse | null>(null);
  const [friendStats, setFriendStats] = useState<FriendStatsResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  // Dragon auth state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval>>();

  // ── Language toggle ──
  const handleLanguageToggle = useCallback(() => {
    setLanguage((prev) => {
      const next: Language = prev === 'uk' ? 'en' : 'uk';
      try {
        localStorage.setItem(STORAGE_KEY_LANG, next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  // ── Initialize ──
  useEffect(() => {
    initTelegramApp();
    const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const queryCode = urlParams?.get('code')?.trim();
    const savedCode = sessionStorage.getItem(STORAGE_KEY_SESSION_CODE);
    const codeToTry = queryCode || savedCode;
    if (codeToTry) {
      doAuth(codeToTry);
    } else {
      setGameState('unauthorized');
    }
  }, []);

  // ── Polling ──
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const [paymentResult, timerResult] = await Promise.all([
          getPaymentStatus(),
          getBattleTimer(),
        ]);
        setPayment(paymentResult);
        setBattleTimer(timerResult);
      } catch {
        // Silently fail on polling errors
      }
    }, POLL_INTERVAL);
  }, []);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // ── Auth flow ──
  const doAuth = useCallback(
    async (codeToTry?: string) => {
      setIsAuthenticating(true);
      setPasswordError('');
      try {
        const authResult = await authenticate(codeToTry);

        if (!authResult.success || !authResult.player) {
          const err = authResult.error ?? t(language, 'valhallaWarning');
          setPasswordError(err);
          setErrorMessage(err);
          setGameState('unauthorized');
          return false;
        }

        if (codeToTry) {
          try {
            sessionStorage.setItem(STORAGE_KEY_SESSION_CODE, codeToTry);
          } catch {
            // ignore
          }
        }

        setPlayer(authResult.player);

        // Fetch payment status and battle timer concurrently
        const [paymentResult, timerResult] = await Promise.all([
          getPaymentStatus(),
          getBattleTimer().catch(() => null),
        ]);
        setPayment(paymentResult);
        if (timerResult) {
          setBattleTimer(timerResult);
        }

        setIsPasswordModalOpen(false);
        setGameState('game');

        // Start polling only in real Telegram environment
        if (isTelegramEnvironment()) {
          startPolling();
        }
        return true;
      } catch (err) {
        const msg = (err as Error).message || t(language, 'valhallaWarning');
        setPasswordError(msg);
        setErrorMessage(msg);
        setGameState('unauthorized');
        return false;
      } finally {
        setIsAuthenticating(false);
      }
    },
    [language, startPolling],
  );

  // ── Dragon click — open password modal ──
  const handleDragonClick = useCallback(() => {
    setPasswordError('');
    setIsPasswordModalOpen(true);
  }, []);

  const handlePasswordSubmit = useCallback(
    async (code: string) => {
      await doAuth(code);
    },
    [doAuth],
  );

  const handlePasswordModalClose = useCallback(() => {
    setIsPasswordModalOpen(false);
    setPasswordError('');
  }, []);

  // ── Hero click — show stats ──
  const handleHeroClick = useCallback(async () => {
    if (isModalLoading) return;
    setIsModalLoading(true);
    try {
      const [stats] = await Promise.all([
        getPlayerStats(),
        sleep(MODAL_LOAD_DELAY),
      ]);
      setPlayerStats(stats);
      setGameState('player_stats');
    } catch (err) {
      console.error('Failed to get player stats:', err);
      setErrorMessage((err as Error).message || 'Failed to load player stats');
      setGameState('error');
    } finally {
      setIsModalLoading(false);
    }
  }, [isModalLoading]);

  // ── Friend click — show stats ──
  const handleFriendClick = useCallback(
    async (friendName?: string, buffIndex?: number, friendCode?: string) => {
      if (isModalLoading) return;
      setIsModalLoading(true);
      try {
        const [stats] = await Promise.all([
          getFriendStats(friendName, buffIndex, friendCode),
          sleep(MODAL_LOAD_DELAY),
        ]);
        setFriendStats(stats);
        setGameState('friend_stats');
      } catch (err) {
        console.error('Failed to get friend stats:', err);
        setErrorMessage((err as Error).message || 'Failed to load friend stats');
        setGameState('error');
      } finally {
        setIsModalLoading(false);
      }
    },
    [isModalLoading],
  );

  // ── Coin click — show payment modal ──
  const handleCoinClick = useCallback(async () => {
    if (isModalLoading) return;
    setIsModalLoading(true);
    await sleep(MODAL_LOAD_DELAY);
    setGameState('payment_modal');
    setIsModalLoading(false);
  }, [isModalLoading]);

  // ── Timer click — show battle schedule modal ──
  const handleTimerClick = useCallback(async () => {
    if (isModalLoading) return;
    setIsModalLoading(true);
    await sleep(MODAL_LOAD_DELAY);
    setGameState('battle_schedule');
    setIsModalLoading(false);
  }, [isModalLoading]);

  // ── Info click — show game guide / help modal ──
  const handleInfoClick = useCallback(async () => {
    if (isModalLoading) return;
    setIsModalLoading(true);
    await sleep(MODAL_LOAD_DELAY);
    setGameState('info');
    setIsModalLoading(false);
  }, [isModalLoading]);

  // ── Logout click — show confirmation modal ──
  const handleLogoutClick = useCallback(async () => {
    if (isModalLoading) return;
    setIsModalLoading(true);
    await sleep(MODAL_LOAD_DELAY);
    setGameState('logout_confirm');
    setIsModalLoading(false);
  }, [isModalLoading]);

  // ── Confirm logout ──
  const handleLogoutConfirm = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      try {
        sessionStorage.removeItem(STORAGE_KEY_SESSION_CODE);
      } catch {
        // ignore
      }
      await logout().catch(() => null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoggingOut(false);
      setPlayer(null);
      setGameState('unauthorized');
    }
  }, []);

  // ── Submit payment ──
  const handlePaymentSubmit = useCallback(
    async (method: PaymentMethod) => {
      try {
        const result = await submitPayment(method);
        if (result.success) {
          setPayment((prev) => ({ ...prev, status: 'submitted' }));
          setGameState('payment_submitted');

          // Auto-close submitted state after 2s
          setTimeout(() => {
            setGameState('game');
          }, 2000);
        }
      } catch (err) {
        setErrorMessage((err as Error).message);
        setGameState('error');
      }
    },
    [],
  );

  // ── Close modals ──
  const handleCloseModal = useCallback(() => {
    setGameState('game');
  }, []);

  // ── Retry ──
  const handleRetry = useCallback(() => {
    const savedCode = sessionStorage.getItem(STORAGE_KEY_SESSION_CODE);
    if (savedCode) {
      doAuth(savedCode);
    } else {
      setGameState('unauthorized');
    }
  }, [doAuth]);

  // ── Render ──
  if (gameState === 'loading') {
    return <LoadingScreen language={language} />;
  }

  if (gameState === 'unauthorized' || !player) {
    return (
      <>
        <EmptyBattlefieldCanvas
          language={language}
          onDragonClick={handleDragonClick}
        />
        {isPasswordModalOpen && (
          <PasswordModal
            onSubmit={handlePasswordSubmit}
            onClose={handlePasswordModalClose}
            isLoading={isAuthenticating}
            errorMessage={passwordError}
            language={language}
          />
        )}
      </>
    );
  }

  if (gameState === 'error') {
    return <ErrorModal message={errorMessage} onRetry={handleRetry} language={language} />;
  }

  return (
    <>
      <GameCanvas
        player={player}
        payment={payment}
        gameState={gameState}
        onStateChange={setGameState}
        onHeroClick={handleHeroClick}
        onFriendClick={handleFriendClick}
        onCoinClick={handleCoinClick}
        battleTimer={battleTimer}
        language={language}
        onTimerClick={handleTimerClick}
        onLanguageToggle={handleLanguageToggle}
        onInfoClick={handleInfoClick}
        onLogoutClick={handleLogoutClick}
        isModalLoading={isModalLoading}
      />

      {isModalLoading && (
        <LightningLoadingOverlay language={language} />
      )}

      {gameState === 'player_stats' && playerStats && (
        <StatsModal
          name={player.name}
          coinsFirst={playerStats.coins_first_player ?? playerStats.coins_first ?? 0}
          coinsFinal={playerStats.coins_final_player ?? playerStats.coins_final ?? 0}
          coinsTax={
            playerStats.coins_tax_player ??
            playerStats.coins_tax ??
            playerStats.coinsSpent ??
            0
          }
          month={playerStats.month}
          language={language}
          onClose={handleCloseModal}
        />
      )}

      {gameState === 'friend_stats' && friendStats && (
        <StatsModal
          name={friendStats.name}
          coinsFirst={
            friendStats.coins_first_buff ??
            friendStats.coins_first_buff1 ??
            friendStats.coins_first_buff2 ??
            friendStats.coins_first_buff3 ??
            0
          }
          coinsFinal={
            friendStats.coins_final_buff ??
            friendStats.coins_final_buff1 ??
            friendStats.coins_final_buff2 ??
            friendStats.coins_final_buff3 ??
            0
          }
          coinsTax={
            friendStats.coins_tax_buff ??
            friendStats.coins_tax_buff1 ??
            friendStats.coins_tax_buff2 ??
            friendStats.coins_tax_buff3 ??
            friendStats.coinsSpent ??
            0
          }
          month={friendStats.month}
          language={language}
          onClose={handleCloseModal}
        />
      )}

      {gameState === 'payment_modal' && (
        <PaymentModal
          payment={payment}
          amount={payment.amount}
          language={language}
          onSelectMethod={handlePaymentSubmit}
          onClose={handleCloseModal}
        />
      )}

      {gameState === 'battle_schedule' && (
        <BattleScheduleModal
          currentPhase={battleTimer?.phase}
          language={language}
          onClose={handleCloseModal}
        />
      )}

      {gameState === 'info' && (
        <InfoModal
          language={language}
          onLogout={handleLogoutClick}
          onClose={handleCloseModal}
        />
      )}

      {gameState === 'logout_confirm' && (
        <LogoutModal
          language={language}
          isLoading={isLoggingOut}
          onConfirm={handleLogoutConfirm}
          onCancel={handleCloseModal}
        />
      )}

      {gameState === 'payment_submitted' && (
        <div className="pixel-overlay">
          <div className="pixel-modal">
            <div className="pixel-modal-title">⏳ {t(language, 'submitted')}</div>
            <div className="pixel-modal-divider" />
            <div className="pixel-modal-text">
              {t(language, 'paymentSubmitted')}
              <br />
              {t(language, 'waitingConfirmation')}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
